import { CalendarDays, Check, CheckSquare, ChevronDown, Mail, StickyNote } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { safePushState } from '@/lib/iosPwaNavigation';
import { useUIStore, type Workspace } from '@/stores/uiStore';
import { navigateToMailView } from '@/features/mailbox/mailViewNavigation';

const APPS: Array<{
  id: Workspace;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}> = [
  { id: 'mail', label: 'Mail', icon: Mail },
  { id: 'todo', label: 'Tasks', icon: CheckSquare },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
];

const DEFAULT_URLS: Record<Workspace, string> = {
  mail: '#/',
  todo: '#/tasks',
  calendar: '#/calendar',
  notes: '#/notes',
};

/**
 * The app switcher: one plum pill naming the current workspace. Round 4 moved
 * it out of the rail and into the combined top row, where it leads the
 * sub-view tabs so the row reads left to right: which app › which view of it.
 * Mobile switches apps via the dock's flipped state, so this renders
 * desktop-only wherever it's placed.
 */
export function AppSwitcher({ className }: { className?: string }) {
  const activeWorkspace = useUIStore((state) => state.activeWorkspace);
  const setActiveWorkspace = useUIStore((state) => state.setActiveWorkspace);
  const mailView = useUIStore((state) => state.mailView);

  const activeApp = APPS.find((app) => app.id === activeWorkspace) ?? APPS[0];
  const ActiveIcon = activeApp.icon;

  const handleSelect = (workspace: Workspace) => {
    if (workspace === activeWorkspace) {
      // Re-selecting Mail from a sub-view returns to the mailbox.
      if (workspace === 'mail' && mailView !== 'mailbox') {
        navigateToMailView('mailbox');
      }
      return;
    }
    // Push the workspace default URL immediately so the URL is never stale
    // during the new workspace's initialization/loading phase
    safePushState(DEFAULT_URLS[workspace]);
    setActiveWorkspace(workspace);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'shrink-0 gap-2 rounded-full bg-sidebar px-4 font-semibold text-sidebar-accent-foreground',
            'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            className
          )}
        >
          <ActiveIcon className="h-4 w-4" />
          {activeApp.label}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {APPS.map(({ id, label, icon: Icon }) => (
          <DropdownMenuItem key={id} onClick={() => handleSelect(id)} className="gap-2">
            <Icon className="h-4 w-4" />
            {label}
            {id === activeWorkspace && <Check className="ml-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
